import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { userRoles } from '../../data/mockData'
import api from '../../services/api'
import { FileText, Linkedin, Phone, Mail, Briefcase, IdCard } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

/**
 * Consultants page — Admin and PM can browse all active consultants,
 * view their profile details and download their CV.
 */
const ConsultantsPage = () => {
  const { t } = useTranslation('consultants')
  const { user } = useAuth()
  const [consultants, setConsultants] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [cvLoading, setCvLoading] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get('/users/')
      .then(r => setConsultants((r.data || []).filter(u => u.role === 'Consultant')))
      .catch(() => setError('Failed to load consultants'))
      .finally(() => setLoading(false))
  }, [])

  const downloadCv = async (consultantId, name) => {
    setCvLoading(consultantId)
    try {
      const resp = await api.get(`/users/${consultantId}/cv`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(name || 'consultant').replace(/\s+/g, '_')}_CV`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      alert(e.response?.data?.detail || 'No CV on file for this consultant.')
    } finally {
      setCvLoading(null)
    }
  }

  const filtered = consultants.filter(c => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.specialization || '').toLowerCase().includes(q) ||
      (c.about || '').toLowerCase().includes(q)
    )
  })

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-linear-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">{t('consultants')}</h1>
        <p className="mt-1 text-sm opacity-90">{consultants.length} consultant{consultants.length !== 1 ? 's' : ''} in the system</p>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, specialization or keywords in their summary…"
          className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
        />
        <svg className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Briefcase className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No consultants found{search ? ` for "${search}"` : ''}.</p>
        </div>
      )}

      {/* Consultant cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map(c => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
            {/* Avatar + name */}
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-linear-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                {(c.full_name || 'C').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{c.full_name}</h3>
                {c.consultant_code && (
                  <p className="text-[11px] font-mono text-gray-400 mt-0.5">{c.consultant_code}</p>
                )}
                {c.specialization && (
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">{c.specialization}</span>
                )}
              </div>
              {/* Status badge */}
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                c.consultant_status === 'Active' ? 'bg-green-100 text-green-700' :
                c.consultant_status === 'Applied' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {c.consultant_status || 'Active'}
              </span>
            </div>

            {/* About / professional summary */}
            {c.about && (
              <p className="text-xs text-gray-600 mb-3 line-clamp-3">{c.about}</p>
            )}

            {/* Contact details — personal data only shows for Admin or the consultant themselves */}
            <div className="space-y-1.5 text-xs text-gray-600 mb-4">
              {c.email ? (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-gray-400" />
                  <span className="truncate">{c.email}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-gray-400 italic">
                  <Mail className="w-3.5 h-3.5 text-gray-300" />
                  <span>{t('contact_hidden')}</span>
                </div>
              )}
              {c.phone_number && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-gray-400" />
                  <span data-private>{c.phone_number}</span>
                </div>
              )}
              {c.linkedin_url && (
                <div className="flex items-center gap-1.5">
                  <Linkedin className="w-3.5 h-3.5 text-blue-500" />
                  <a
                    href={c.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                  >
                    LinkedIn profile
                  </a>
                </div>
              )}
              {c.years_of_experience && (
                <div className="flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                  <span>{c.years_of_experience} years experience</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-3 border-t border-gray-100">
              {c.email && (
                <button
                  onClick={() => window.location.href = `mailto:${c.email}?subject=3 Pillars Project Enquiry`}
                  className="flex-1 text-xs py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-center"
                >
                  ✉️ Contact
                </button>
              )}
              {/* CV download if on file; else fall back to their LinkedIn; else nothing */}
              {c.has_cv ? (
                <button
                  onClick={() => downloadCv(c.id, c.full_name)}
                  disabled={cvLoading === c.id}
                  className="flex items-center gap-1 flex-1 justify-center text-xs py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                  title={t('t_download_cv')}
                >
                  <FileText className="w-3.5 h-3.5" />
                  {cvLoading === c.id ? 'Loading…' : 'Download CV'}
                </button>
              ) : c.linkedin_url ? (
                <a
                  href={c.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 flex-1 justify-center text-xs py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
                  title={t('t_no_cv')}
                >
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </a>
              ) : (
                <span className="flex items-center gap-1 flex-1 justify-center text-xs py-1.5 border border-gray-200 rounded-lg text-gray-400">
                  <FileText className="w-3.5 h-3.5" /> No CV yet
                </span>
              )}
            </div>

            {/* Everything else about this person lives on the Admin people screen — bank
                details, identity documents, onboarding progress and what is still missing.
                Without this link an admin looking here reasonably concluded the system did
                not hold any of it. */}
            {user?.role === userRoles.ADMIN && (
              <Link
                to={`/admin/people?user=${c.id}&from=/consultants`}
                className="mt-2 flex items-center justify-center gap-1.5 w-full text-xs py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                title="Bank details, identity documents, onboarding status and outstanding items"
              >
                <IdCard className="w-3.5 h-3.5" /> Full profile &amp; bank details
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default ConsultantsPage
